import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { useOrganization } from "@/auth/organization";
import { useToast } from "@/components/ui";
import { Attempt, type EmailActionResult } from "@/lib/mailboxes";

/** What a confirmed change is called in the toast that confirms it, by what was done. */
export const DONE: Record<string, string> = {
  create: "Created",
  update: "Saved",
  suspend: "Suspended",
  resume: "Resumed",
  reset: "Password reset requested",
  recover: "Recovered",
  delete: "Deleted",
  refresh: "Status refreshed",
};

/**
 * One change to the business's email: a request with its own `Idempotency-Key` (kept across retries that got no
 * answer, replaced once the server has answered). A confirmed change shows a toast; one the provider refused or did
 * not confirm shows on the item itself (its problem, reference and Refresh), because that is where the person acts.
 */
export function useEmailMutation<V>(
  call: (orgId: string, vars: V, key: string) => Promise<EmailActionResult>,
  opts: { done?: string; onResult?: (result: EmailActionResult, vars: V) => void } = {},
) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const attempt = useRef(new Attempt());
  return useMutation({
    mutationFn: (vars: V) => call(orgId, vars, attempt.current.key()),
    onSuccess: async (result, vars) => {
      attempt.current.finished();
      await qc.invalidateQueries({ queryKey: ["mailboxes", orgId, "overview"] });
      if (result.outcome === "succeeded") {
        if (opts.done) toast.show({ tone: "success", title: opts.done });
      } else if (result.outcome === "pending") {
        toast.show({ tone: "info", title: "Still waiting for the email provider", body: result.message });
      }
      opts.onResult?.(result, vars);
    },
    onError: (error) => attempt.current.finished(error),
  });
}
